import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {Option, OptionGroup} from '../../../shared/models/option';
import {User} from '../../../shared/models/user';
import {ObjectModel, ObjectType} from '../../../shared/models/object.model';
import {UsersService} from '../../../core/services/users.service';
import {UtilsService} from '../../../core/services/utils.service';
import {SessionService} from '../../../core/services/session.service';
import {Subject, tap} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {NgClass} from '@angular/common';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {PillOption, Role, RoleOptions, rolesToPillOptions} from 'src/shared/models/fga/role';
import {PillMultiselectComponent} from '../pill-multiselect/pill-multiselect.component';
import {PermissionsService} from 'src/core/services/permissions.service';
import {ObjectGrantee} from 'src/shared/models/fga/grantees';

@Component({
  selector: 'user-sharing-dialog-box',
  templateUrl: './user-sharing-dialog-box.component.html',
  styleUrls: ['./user-sharing-dialog-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    NgClass,
    PopoverMenuComponent,
    SpinnerComponent,
    PillMultiselectComponent,
  ],
})
export class UserSharingDialogBoxComponent implements OnInit, AfterViewInit {
  @Input() object: ObjectModel;
  @Output() close: EventEmitter<null> = new EventEmitter<null>();

  selectedUser: Option;
  selectedRoles: Role[] = [Role.VALUE_WRITER, Role.RESPONDER];
  RoleOptions = RoleOptions;

  scrollableHeightPopoverPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
  }];
  grantees: ObjectGrantee[] = [];
  users: User[] = [];
  inputError: string;
  showSearchDropdown = false;

  isLoading = false;

  public userInput: string = '';

  @ViewChild('dialogBoxInput') private inputEl: ElementRef<HTMLInputElement>;

  rolePickerOpenMenu$ = new Subject<void>();


  get selectedRoleOptions(): PillOption<Role>[] {
    return rolesToPillOptions(this.selectedRoles);
  }


  get scrollableHeightMenuOptions(): OptionGroup {
    const isEmailSearch = this.isEmail(this.userInput);

    if (this.users.length) {
      return new OptionGroup(
        this.users.map(user =>
          ({id: user.ID, name: user.Username + (isEmailSearch ? ` - ${user.Email}` : '')}),
        ),
      );
    }
  };


  constructor(
    private usersService: UsersService,
    private session: SessionService,
    private permissionsService: PermissionsService,
  ) {
  }


  ngOnInit(): void {
    this.fetchGrants();
  }


  private fetchGrants() {
    this.permissionsService.queryObjectGrantees(this.object.id, this.object.type).subscribe({
      next: (grantees) => {
        this.grantees = grantees;
      },
      error: ({message}) => {
        this.showError(message);
      },
    });
  }


  ngAfterViewInit() {
    this.inputEl?.nativeElement.focus();
  }


  search(): void {
    this.isLoading = true;

    if (this.isEmail(this.userInput)) {
      this.usersService.findUsers$('Email', this.userInput)
        .pipe(tap(users => {
          if (!users?.length) {
            throw new Error('No user with the provided email address could be found.');
          }
        })).subscribe({
        next: (users) => {
          this.users = users;
          if (users.length === 1) {
            this.selectedUser = {id: users[0].ID, name: users[0].Username};
            this.showSearchDropdown = false;
            this.checkIfUserCanBeAdded(this.selectedUser);
          } else {
            this.showSearchDropdown = true;
          }
        },
        error: ({message}) => {
          this.showError(message);
        },
        complete: () => this.isLoading = false,
      });
    } else {
      this.usersService.findUser$('Username', this.userInput).subscribe({
        next: (user) => {
          this.users = [user];
          this.selectedUser = {id: user.ID, name: user.Username};
          this.showSearchDropdown = false;
          this.checkIfUserCanBeAdded(this.selectedUser);
        },
        error: ({message}) => {
          this.showError(message);
        },
        complete: () => this.isLoading = false,
      });
    }
  }


  checkIfUserCanBeAdded(user: Option): boolean {
    // Check if user is trying to add themselves
    if (user.name === this.session.user.Username) {
      this.showError(`You cannot add yourself.`);
      this.selectedUser = null;
      return false;
    }
    // Check if user is the owner
    if (user.id === this.object.owner) {
      this.showError(`The provided user is already the owner of this ${this.object.type}.`);
      this.selectedUser = null;
      return false;
    }

    // Check if user is already in the shared list
    const existingGrantee = this.grantees.find(g => g.Type === ObjectType.USER && g.ID === user.id);
    if (existingGrantee) {
      this.showError(`The provided user already has access to this ${this.object.type}.`);
      this.selectedUser = null;
      return false;
    }

    return true;
  }


  showError(error?: string) {
    this.isLoading = false;

    if (error) {
      this.inputError = error;
      return;
    }
    this.inputError = 'Something went wrong. Please try again.';
  }


  closeModal() {
    this.close.emit();
  };


  isEmail(string: string) {
    const re = /\S+@\S+\.\S+/;
    return re.test(string);
  }


  onClickedOption(user: Option) {
    this.selectedUser = user;
    this.showSearchDropdown = false;
    this.checkIfUserCanBeAdded(this.selectedUser);
  }


  submit() {
    // Final check before submitting
    if (!this.selectedUser || !this.checkIfUserCanBeAdded(this.selectedUser)) {
      return;
    }

    this.isLoading = true;
    this.permissionsService.assignRolesToUser({
      UserID: this.selectedUser.id,
      ObjectID: this.object.id,
      ObjectType: this.object.type,
      Roles: this.selectedRoles,
    }).subscribe({
      next: () => {
        this.closeModal();
      },
      error: (err) => {
        this.showError(UtilsService.extractErrorMessage(err.error));
      },
      complete: () => this.isLoading = false,
    });
  }


  executeCurrentAction(): void {
    if (this.selectedUser) {
      this.submit();
    } else {
      this.search();
    }
  }


  onUserInputChange() {
    this.users = [];
    this.inputError = null;
    this.selectedUser = null;
  }


  onRemovingRole(roleOption: PillOption) {
    this.selectedRoles = this.selectedRoles.filter(r => r !== (roleOption.key as Role));
  }


  onAddingRole(roleOption: PillOption) {
    this.selectedRoles = [...this.selectedRoles, roleOption.key as Role];
  }


  get isActionButtonDisabled(): boolean {
    return !!this.inputError || !this.userInput.length;
  }


  onAddRoleClick(): void {
    this.rolePickerOpenMenu$.next();
  }
}
